import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./MyProfile.css";
import OrganizationTabs from "./OrganizationTabs";

function MultiSelectDropdown({ options, selected, onChange, placeholder = "Select...", disabled = false, loading = false, emptyMessage = "No options available", searchable = true }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedSet = new Set((selected || []).map(String));
  const filteredOptions = search ? options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase())) : options;

  const toggleOption = (value) => {
    const stringValue = String(value);
    if (selectedSet.has(stringValue)) {
      onChange(selected.filter((item) => String(item) !== stringValue));
    } else {
      onChange([...selected, value]);
    }
  };

  const removeChip = (value, e) => {
    e.stopPropagation();
    onChange(selected.filter((item) => String(item) !== String(value)));
  };

  const selectAll = () => {
    const filteredValues = filteredOptions.map((option) => option.value);
    const mergedValues = [...selected, ...filteredValues.filter((value) => !selectedSet.has(String(value)))];
    onChange(mergedValues);
  };

  const clearAll = () => onChange([]);

  const selectedOptions = options.filter((option) => selectedSet.has(String(option.value)));

  return (
    <div className={`msdd ${open ? "open" : ""}`} ref={wrapperRef}>
      <div className={`msdd-control ${disabled ? "disabled" : ""}`} onClick={() => !disabled && setOpen((previous) => !previous)}>
        {selectedOptions.length === 0 ? (
          <span className="msdd-placeholder">{loading ? "Loading..." : placeholder}</span>
        ) : selectedOptions.length <= 2 ? (
          selectedOptions.map((option) => (
            <span className="msdd-chip" key={option.value}>
              {option.label}
              <button type="button" className="msdd-chip-remove" onClick={(e) => removeChip(option.value, e)} aria-label={`Remove ${option.label}`}>✕</button>
            </span>
          ))
        ) : (
          <span className="msdd-count-label">{selectedOptions.length} selected</span>
        )}
        <span className="msdd-caret" />
      </div>

      {open && !disabled && (
        <div className="msdd-panel">
          {searchable && options.length > 5 && (
            <input type="text" className="msdd-search" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
          )}

          <div className="msdd-options">
            {loading ? (
              <div className="msdd-empty">Loading options...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="msdd-empty">{emptyMessage}</div>
            ) : (
              filteredOptions.map((option) => {
                const isChecked = selectedSet.has(String(option.value));
                return (
                  <label className={`msdd-option ${isChecked ? "checked" : ""}`} key={option.value}>
                    <input type="checkbox" checked={isChecked} onChange={() => toggleOption(option.value)} />
                    <span className="msdd-option-text">{option.label}</span>
                  </label>
                );
              })
            )}
          </div>

          {!loading && filteredOptions.length > 0 && (
            <div className="msdd-footer">
              <button type="button" className="msdd-footer-btn" onClick={selectAll} disabled={filteredOptions.every((option) => selectedSet.has(String(option.value)))}>Select all</button>
              <button type="button" className="msdd-footer-btn" onClick={clearAll} disabled={selected.length === 0}>Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const emptyForm = {
  name: "", legal_name: "", trade_name: "", code: "", contact_email: "", contact_phone: "", portal_slug: "", custom_domain: "", website: "", description: "", workspace_type: "", organisation_type: "", default_currency: "", default_timezone: "", industry_ids: [], service_scope_ids: [], logo: null
};

const WORKSPACE_TYPES = [
  { value: "ORGANISATION", label: "Organisation tenant" },
  { value: "PERSONAL", label: "Personal workspace" }
];

const ORGANISATION_TYPES = [
  { value: "CLIENT", label: "Client" },
  { value: "PMC", label: "PMC" },
  { value: "EPC_CONTRACTOR", label: "EPC Contractor" },
  { value: "MANUFACTURER", label: "Manufacturer" },
  { value: "INSPECTION_BODY", label: "Inspection Body" },
  { value: "SHIPOWNER", label: "Shipowner" },
  { value: "OPERATOR", label: "Operator" },
  { value: "TRAINING_ORGANISATION", label: "Training Organisation" },
  { value: "OTHER", label: "Other" }
];

const MyProfile = () => {
  const [formData, setFormData] = useState({ ...emptyForm });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [allScopeCatalogs, setAllScopeCatalogs] = useState([]);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [loadingScopes, setLoadingScopes] = useState(false);

  useEffect(() => {
    const storedTenantUser = localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      Swal.fire({ icon: "error", title: "Tenant information missing", text: "Please login again." });
      setLoading(false);
      return;
    }

    try {
      const tenantUser = JSON.parse(storedTenantUser);

      if (!tenantUser.tenant_id) {
        Swal.fire({ icon: "error", title: "Tenant ID missing", text: "Tenant information could not be found." });
        setLoading(false);
        return;
      }

      setTenantId(Number(tenantUser.tenant_id));
    } catch (error) {
      console.error("Invalid tenant_user:", error);
      Swal.fire({ icon: "error", title: "Invalid tenant session", text: "Please login again." });
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchIndustries = async () => {
      setLoadingIndustries(true);

      try {
        const response = await fetch(`${API_BASE}/reference-values/`, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" }
        });

        const result = await response.json();

        if (response.ok) {
          const industryData = result?.data || [];
          const filteredIndustries = industryData.filter((item) =>
            item.option_set_value?.option_type === "INDUSTRY" ||
            item.option_set?.option_type === "INDUSTRY" ||
            item.option_type === "INDUSTRY"
          );
          setIndustries(filteredIndustries);
        }
      } catch (error) {
        console.error("Error fetching industries:", error);
      } finally {
        setLoadingIndustries(false);
      }
    };

    fetchIndustries();
  }, []);

  useEffect(() => {
    const fetchScopeCatalogs = async () => {
      setLoadingScopes(true);

      try {
        const response = await fetch(`${API_BASE}/scope-catalogs/`, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" }
        });

        const result = await response.json();

        if (response.ok) setAllScopeCatalogs(result?.data || []);
      } catch (error) {
        console.error("Error fetching scope catalogs:", error);
      } finally {
        setLoadingScopes(false);
      }
    };

    fetchScopeCatalogs();
  }, []);

  useEffect(() => {
    if (tenantId) fetchTenantDetails();
  }, [tenantId]);

  const fetchTenantDetails = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API_BASE}/tenants/${tenantId}/stage1-details/`);

      if (!response.data?.success) {
        Swal.fire({ icon: "error", title: "Unable to load profile", text: "Tenant details could not be loaded." });
        return;
      }

      const tenant = response.data?.data?.tenant;

      if (!tenant) {
        Swal.fire({ icon: "error", title: "Tenant details not found", text: "Basic information could not be found." });
        return;
      }

      let industryIds = [];

      if (Array.isArray(tenant.industry_ids)) {
        industryIds = tenant.industry_ids;
      } else if (tenant.industry_ids) {
        try {
          const parsed = JSON.parse(tenant.industry_ids);
          industryIds = Array.isArray(parsed) ? parsed : [tenant.industry_ids];
        } catch {
          industryIds = String(tenant.industry_ids).split(",").map((item) => item.trim()).filter(Boolean);
        }
      }

      let serviceScopeIds = [];

      if (Array.isArray(tenant.service_scope_ids)) {
        serviceScopeIds = tenant.service_scope_ids;
      } else if (tenant.service_scope_ids) {
        try {
          const parsed = JSON.parse(tenant.service_scope_ids);
          serviceScopeIds = Array.isArray(parsed) ? parsed : [tenant.service_scope_ids];
        } catch {
          serviceScopeIds = String(tenant.service_scope_ids).split(",").map((item) => item.trim()).filter(Boolean);
        }
      }

      setFormData({
        name: tenant.name || "",
        legal_name: tenant.legal_name || "",
        trade_name: tenant.trade_name || "",
        code: tenant.code || "",
        contact_email: tenant.contact_email || "",
        contact_phone: tenant.contact_phone || "",
        portal_slug: tenant.portal_slug || "",
        custom_domain: tenant.custom_domain || "",
        website: tenant.website || "",
        description: tenant.description || "",
        workspace_type: tenant.workspace_type || "",
        organisation_type: tenant.organisation_type || "",
        default_currency: tenant.default_currency || "",
        default_timezone: tenant.default_timezone || "",
        industry_ids: industryIds,
        service_scope_ids: serviceScopeIds,
        logo: tenant.logo || null
      });

      if (tenant.logo) {
        const logoUrl = tenant.logo.startsWith("http") ? tenant.logo : `${API_BASE}${tenant.logo}`;
        setLogoPreview(logoUrl);
      } else {
        setLogoPreview(null);
      }
    } catch (error) {
      console.error("Error fetching tenant details:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load profile",
        text: error.response?.data?.message || error.response?.data?.detail || "Something went wrong while loading tenant information."
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!formData.industry_ids || formData.industry_ids.length === 0) {
      setAvailableScopes([]);
      setFormData((previous) => previous.service_scope_ids.length === 0 ? previous : { ...previous, service_scope_ids: [] });
      return;
    }

    const selectedIndustryIds = industries
      .filter((industry) => formData.industry_ids.includes(industry.label || industry.value))
      .map((industry) => String(industry.id));

    const filteredScopes = allScopeCatalogs.filter((scope) => selectedIndustryIds.includes(String(scope.industry)));

    setAvailableScopes(filteredScopes);

    setFormData((previous) => {
      const validScopeNames = filteredScopes.map((scope) => scope.scope_name);
      const prunedScopes = previous.service_scope_ids.filter((scopeName) => validScopeNames.includes(scopeName));

      return prunedScopes.length === previous.service_scope_ids.length ? previous : { ...previous, service_scope_ids: prunedScopes };
    });
  }, [formData.industry_ids, allScopeCatalogs, industries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleIndustryChange = (newValues) => {
    setFormData((previous) => ({ ...previous, industry_ids: newValues }));
  };

  const handleScopeChange = (newValues) => {
    setFormData((previous) => ({ ...previous, service_scope_ids: newValues }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire({ icon: "error", title: "Invalid file", text: "Please select a valid image file." });
      return;
    }

    setSelectedLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const getLogoUrl = () => {
    if (!logoPreview) return null;
    if (logoPreview.startsWith("http") || logoPreview.startsWith("blob:")) return logoPreview;
    return `${API_BASE}${logoPreview}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({ icon: "error", title: "Tenant ID missing", text: "Unable to update tenant profile." });
      return;
    }

    if (!formData.name.trim()) {
      Swal.fire({ icon: "warning", title: "Tenant name required", text: "Please enter the tenant name." });
      return;
    }

    if (!formData.code.trim()) {
      Swal.fire({ icon: "warning", title: "Tenant code required", text: "Please enter the tenant code." });
      return;
    }

    if (!formData.portal_slug.trim()) {
      Swal.fire({ icon: "warning", title: "Portal slug required", text: "Please enter the portal slug." });
      return;
    }

    try {
      setSaving(true);

      const payload = new FormData();

      payload.append("name", formData.name);
      payload.append("legal_name", formData.legal_name);
      payload.append("trade_name", formData.trade_name);
      payload.append("code", formData.code);
      payload.append("contact_email", formData.contact_email);
      payload.append("contact_phone", formData.contact_phone);
      payload.append("portal_slug", formData.portal_slug);
      payload.append("custom_domain", formData.custom_domain);
      payload.append("website", formData.website);
      payload.append("description", formData.description);
      payload.append("workspace_type", formData.workspace_type);
      payload.append("organisation_type", formData.organisation_type);
      payload.append("default_currency", formData.default_currency);
      payload.append("default_timezone", formData.default_timezone);
      payload.append("industry_ids", JSON.stringify(formData.industry_ids || []));
      payload.append("service_scope_ids", JSON.stringify(formData.service_scope_ids || []));

      if (selectedLogo) payload.append("logo", selectedLogo);

      const response = await axios.put(`${API_BASE}/tenants/${tenantId}/`, payload, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data?.success) {
        Swal.fire({
          icon: "success",
          title: "Profile Updated",
          text: "Tenant profile has been updated successfully.",
          confirmButtonText: "OK"
        });

        setSelectedLogo(null);
        await fetchTenantDetails();
      } else {
        Swal.fire({
          icon: "error",
          title: "Update failed",
          text: response.data?.message || "Tenant profile could not be updated."
        });
      }
    } catch (error) {
      console.error("Error updating tenant profile:", error);

      const errorData = error.response?.data;
      let errorMessage = errorData?.message || errorData?.detail || "Something went wrong while updating the tenant profile.";

      if (typeof errorData === "object" && !errorData.message) {
        const validationErrors = Object.entries(errorData).map(([field, messages]) => {
          if (Array.isArray(messages)) return `${field}: ${messages.join(", ")}`;
          return `${field}: ${messages}`;
        }).join("\n");

        if (validationErrors) errorMessage = validationErrors;
      }

      Swal.fire({ icon: "error", title: "Unable to update profile", text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const industryOptions = industries.map((industry) => ({
    value: industry.label || industry.value,
    label: industry.label || industry.value
  }));

  const scopeOptions = availableScopes.map((scope) => ({
    value: scope.scope_name,
    label: `${scope.scope_name} - ${scope.code}`
  }));

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="myprofile-page">
            <div className="myprofile-loading">Loading profile information...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="myprofile-page">
          <OrganizationTabs/>
          <div className="myprofile-page-header">
            <div>
              <h2>My Profile</h2>
              <p>View and update your tenant basic information</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="myprofile-card">
              <div className="myprofile-form-body">
                <div className="myprofile-section-title">Basic Information</div>

                <div className="myprofile-form-grid">
                  <div className="myprofile-form-group">
                    <label>Tenant Name <span>*</span></label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Tenant name" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Legal Name</label>
                    <input type="text" name="legal_name" value={formData.legal_name} onChange={handleChange} placeholder="Legal name" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Trade Name</label>
                    <input type="text" name="trade_name" value={formData.trade_name} onChange={handleChange} placeholder="Trade name" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Tenant Code <span>*</span></label>
                    <input type="text" name="code" value={formData.code} onChange={handleChange} placeholder="Tenant code" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Contact Email</label>
                    <input type="email" name="contact_email" value={formData.contact_email} onChange={handleChange} placeholder="Contact email" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Contact Phone</label>
                    <input type="text" name="contact_phone" value={formData.contact_phone} onChange={handleChange} placeholder="Contact phone" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Portal Slug <span>*</span></label>
                    <input type="text" name="portal_slug" value={formData.portal_slug} onChange={handleChange} placeholder="Portal slug" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Custom Domain</label>
                    <input type="text" name="custom_domain" value={formData.custom_domain} onChange={handleChange} placeholder="Custom domain" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Website</label>
                    <input type="url" name="website" value={formData.website} onChange={handleChange} placeholder="Website URL" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Workspace Type</label>
                    <select name="workspace_type" value={formData.workspace_type} onChange={handleChange}>
                      <option value="">Select workspace type</option>
                      {WORKSPACE_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                    </select>
                  </div>

                  <div className="myprofile-form-group">
                    <label>Organisation Type</label>
                    <select name="organisation_type" value={formData.organisation_type} onChange={handleChange}>
                      <option value="">Select organisation type</option>
                      {ORGANISATION_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                    </select>
                  </div>

                  <div className="myprofile-form-group">
                    <label>Currency</label>
                    <input type="text" name="default_currency" value={formData.default_currency} onChange={handleChange} placeholder="Currency" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Timezone</label>
                    <input type="text" name="default_timezone" value={formData.default_timezone} onChange={handleChange} placeholder="Timezone" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Industries</label>
                    <MultiSelectDropdown options={industryOptions} selected={formData.industry_ids} onChange={handleIndustryChange} placeholder="Select industries" loading={loadingIndustries} emptyMessage="No industries found" />
                  </div>

                  <div className="myprofile-form-group">
                    <label>Service Scopes</label>
                    <MultiSelectDropdown options={scopeOptions} selected={formData.service_scope_ids} onChange={handleScopeChange} placeholder="Select scopes" loading={loadingScopes} disabled={formData.industry_ids.length === 0} emptyMessage={formData.industry_ids.length === 0 ? "Select an industry first" : "No scopes found for selected industries"} />
                    <span className="myprofile-hint">
                      {formData.industry_ids.length === 0 ? "Choose an industry to see available scopes" : `${scopeOptions.length} scope${scopeOptions.length === 1 ? "" : "s"} available`}
                    </span>
                  </div>
                </div>

                <div className="myprofile-form-group myprofile-description-group">
                  <label>Description</label>
                  <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Tenant description" rows="5" />
                </div>

                <div className="myprofile-logo-section">
                  <div className="myprofile-logo-section-title">Tenant Logo</div>

                  <div className="myprofile-logo-area">
                    <div className="myprofile-logo-preview">
                      {getLogoUrl() ? <img src={getLogoUrl()} alt="Tenant Logo" /> : <span className="myprofile-logo-empty">No Logo</span>}
                    </div>

                    <div className="myprofile-logo-upload">
                      <label>Change Logo</label>
                      <input type="file" accept="image/*" onChange={handleLogoChange} />
                      <small className="myprofile-logo-help">Select an image file to update the tenant logo.</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="myprofile-form-actions">
              <button type="submit" className="myprofile-save-btn" disabled={saving}>
                {saving ? "Updating..." : "Update Profile"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;